import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  insertNodeAtChildIndex,
  removeNodeFromTree,
} from '../../../components/Tree/helpers/helpers';
import type { NodeBase } from '../../../entities/Node';
import { destructId, getPathById } from '../../../utils/tree/treeNode';
import { selectIsGlobalMarket } from '../../slices/intl';
import { selectTreeDataByCatalogId, updateData } from '../../slices/tree';
import { saveSortOrder } from '../../slices/treeSort';
import { moveCategory as moveCategoryApi } from '../api/categories';
import type { RootState } from '../..';

interface ReorderNodeParams {
  tree: string;
  catalogId: string;
  dragNode: NodeBase;
  dropNode: NodeBase;
  dropPosition: number;
}

interface TreeOperationResult {
  treeData: NodeBase[];
}

/**
 * Reorders a node within its parent's children via drag-and-drop.
 * This action handles reordering within the same parent only.
 *
 * For global market: Calls the API immediately to persist the change.
 * For local market: Saves to sort order for bulk changes.
 */
export const reorderNode = createAsyncThunk(
  'tree/reorderNode',
  async (
    { tree, catalogId, dragNode, dropNode, dropPosition }: ReorderNodeParams,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState;
    const currentTreeData = selectTreeDataByCatalogId(state, catalogId);
    const isGlobalMarket = selectIsGlobalMarket(state);

    // Remove the dragged node from the tree
    const { treeData: treeWithoutNode } = removeNodeFromTree({
      path: getPathById(dragNode.id),
      treeData: currentTreeData,
    }) as TreeOperationResult;

    // Special case: dropping onto the parent means "move to index 0"
    // When dropping onto the parent node (not a sibling), we want to move to first position
    const isDroppingOntoParent = dropNode.id === dragNode.parentId;

    let targetIndex: number;
    let parentChildren: NodeBase[];

    if (isDroppingOntoParent) {
      // Dropping onto parent = move to first position
      targetIndex = 0;
      parentChildren = dropNode.children || [];
    } else {
      // Find the parent node to get the children array
      parentChildren = findParentChildren(treeWithoutNode, dropNode);

      // Calculate the target index based on dropPosition
      // dropPosition is relative to the drop target:
      // - dropPosition < 0: drop before the target node
      // - dropPosition > 0: drop after the target node
      const dropIndex = parentChildren.findIndex(
        (child: NodeBase) => child.id === dropNode.id,
      );
      targetIndex = dropPosition < 0 ? dropIndex : dropIndex + 1;
    }

    // Determine placedAfterId for API call
    const placedAfterId =
      targetIndex < 1 ? undefined : parentChildren[targetIndex - 1]?.categoryId;

    // Insert the dragged node at the new position
    const { treeData: newTreeData } = insertNodeAtChildIndex({
      childIndex: targetIndex,
      newNode: dragNode,
      treeData: treeWithoutNode,
    }) as TreeOperationResult;

    // Get parent category ID from the node's parentId
    // destructId extracts the categoryId from the composite id (e.g., "catalogId:categoryId")
    const parentCategoryId = (
      destructId(dragNode.parentId) as { categoryId?: string }
    )?.categoryId;

    if (isGlobalMarket) {
      // Global market: Call API immediately to persist the change
      // Note: The API accepts an array payload but the types are incomplete
      await dispatch(
        moveCategoryApi({
          catalogId: dragNode.catalogId,
          payload: [
            {
              categoryId: dragNode.categoryId,
              oldParentCategoryId: parentCategoryId,
              newParentCategoryId: parentCategoryId,
              placeAfterCategoryId: placedAfterId,
            },
          ],
        } as unknown as Parameters<typeof moveCategoryApi>[0]),
      );
    } else {
      // Local market: Save to sort order for bulk changes
      const move = {
        currentTree: currentTreeData,
        newTree: newTreeData,
        catalogId,
        parentId: dragNode.parentId,
        categoryId: dragNode.categoryId,
      };

      dispatch(saveSortOrder(move));
    }

    // Update the Redux store
    dispatch(updateData(tree, newTreeData));

    return { success: true };
  },
);

/**
 * Finds the children array of the parent that contains the given node.
 */
function findParentChildren(treeData: NodeBase[], node: NodeBase): NodeBase[] {
  // First try to find using parentId
  if (node.parentId) {
    const parent = findNodeById(treeData, node.parentId);
    if (parent?.children) {
      return parent.children;
    }
  }

  // Fallback: search the tree for a node that has this as a child
  for (const treeNode of treeData) {
    if (treeNode.children?.some((child: NodeBase) => child.id === node.id)) {
      return treeNode.children;
    }

    // Recursively search children
    if (treeNode.children) {
      const found = findParentChildren(treeNode.children, node);
      if (found.length > 0) {
        return found;
      }
    }
  }

  return [];
}

/**
 * Finds a node by its ID in the tree.
 */
function findNodeById(treeData: NodeBase[], id: string): NodeBase | null {
  for (const node of treeData) {
    if (node.id === id) {
      return node;
    }

    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) {
        return found;
      }
    }
  }

  return null;
}
